/**
 * Optional product lenses that still gate a surviving surface.
 *
 * Builds, playtests, the design model, and engine ingest died with their screens.
 * Machine "which CLIs are installed" matching lives on runner tags
 * (`scheduling/eligibility`), not here; the machine registry records installed harnesses.
 * `budgets` stays because the Budgets view is still gated on it.
 */

export const CAPABILITIES = ['budgets'] as const;
export type Capability = (typeof CAPABILITIES)[number];

export function isCapability(value: string): value is Capability {
  return (CAPABILITIES as readonly string[]).includes(value);
}

export const PROJECT_PROFILES = ['game', 'software', 'custom'] as const;
export type ProjectProfile = (typeof PROJECT_PROFILES)[number];

export function isProjectProfile(value: string): value is ProjectProfile {
  return (PROJECT_PROFILES as readonly string[]).includes(value);
}

/**
 * Lightweight, changeable phase label (#3 absorbed into #1).
 * Not a second lifecycle next to the tracker — view emphasis only.
 */
export const PROJECT_PHASES = [
  'preproduction',
  'production',
  'alpha',
  'beta',
  'release',
  'live',
  'continuous',
] as const;
export type ProjectPhase = (typeof PROJECT_PHASES)[number];

export function isProjectPhase(value: string): value is ProjectPhase {
  return (PROJECT_PHASES as readonly string[]).includes(value);
}

export const ENGINE_FAMILIES = [
  'godot',
  'unity',
  'unreal',
  'monogame',
  'custom',
  'none',
] as const;
export type EngineFamily = (typeof ENGINE_FAMILIES)[number];

export function isEngineFamily(value: string): value is EngineFamily {
  return (ENGINE_FAMILIES as readonly string[]).includes(value);
}

/**
 * Controlled vocabulary mapped onto tracker labels (`discipline:<id>`).
 * Nightshift never owns a parallel taxonomy the tracker cannot see (#4).
 */
export const DISCIPLINES = [
  'code',
  'design',
  '3d-art',
  '2d-art',
  'animation',
  'vfx',
  'audio',
  'ui',
  'qa',
  'production',
  'narrative',
  'tech-art',
] as const;
export type Discipline = (typeof DISCIPLINES)[number];

export function isDiscipline(value: string): value is Discipline {
  return (DISCIPLINES as readonly string[]).includes(value);
}

/**
 * Normalised work-item types mapped onto tracker labels (`type:<id>`).
 * Selects refine/implement/review emphasis; not a new issue model (#5).
 */
export const WORK_ITEM_TYPES = [
  'feature',
  'bug',
  'chore',
  'asset',
  'design',
  'performance',
  'platform',
  'docs',
] as const;
export type WorkItemType = (typeof WORK_ITEM_TYPES)[number];

export function isWorkItemType(value: string): value is WorkItemType {
  return (WORK_ITEM_TYPES as readonly string[]).includes(value);
}

export const CAPABILITY_HELP: Record<Capability, string> = {
  budgets:
    'Gateable quality targets — frame time and memory, or SLOs, latency, and bundle size.',
};

export interface ProfileArrangement {
  capabilities: Record<Capability, boolean>;
  phase: ProjectPhase;
  disciplines: Discipline[];
  workItemTypes: WorkItemType[];
}

/**
 * What a named profile arranges when the operator has not overridden a leaf.
 * Changing the profile does not rewrite capability values the project set explicitly.
 */
export const PROFILE_ARRANGEMENTS: Record<ProjectProfile, ProfileArrangement> = {
  game: {
    capabilities: {
      budgets: true,
    },
    phase: 'production',
    disciplines: ['code', 'design', '3d-art', '2d-art', 'animation', 'audio', 'ui', 'qa'],
    workItemTypes: ['feature', 'bug', 'asset', 'design', 'performance'],
  },
  software: {
    capabilities: {
      budgets: true,
    },
    phase: 'continuous',
    disciplines: ['code', 'design', 'qa', 'production'],
    workItemTypes: ['feature', 'bug', 'chore', 'docs', 'performance', 'platform'],
  },
  custom: {
    capabilities: {
      budgets: false,
    },
    phase: 'continuous',
    disciplines: ['code'],
    workItemTypes: ['feature', 'bug', 'chore'],
  },
};
